import {
  Button,
  Paper,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ScheduleIcon from '@material-ui/icons/Schedule';
import * as React from 'react';
import { ExternalOrderService } from 'services';
import { SnackbarContext } from 'components';
import { BoltHoursDialog } from '../BoltOrdersPreviewView/BoltHoursDialog';
import { WebsiteHoursDialog } from '../BoltOrdersPreviewView/WebsiteHoursDialog';

/**
 * Settings that used to crowd the live order board: opening hours and the
 * push to Bolt. They are set once and left alone, so they do not belong on
 * the screen staff watch during service (Lucy 2026-09-06).
 */
const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    maxWidth: 760,
  },
  title: { fontWeight: 700, marginBottom: theme.spacing(0.5) },
  body: { color: theme.palette.text.secondary, marginBottom: theme.spacing(2) },
  actions: { display: 'flex', gap: theme.spacing(1), flexWrap: 'wrap' },
}));

interface Props {
  shopId: number;
}

export const ExternalSettingsView: React.FC<Props> = ({ shopId }) => {
  const classes = useStyles();
  const snackbar = React.useContext(SnackbarContext);
  const [boltHoursOpen, setBoltHoursOpen] = React.useState(false);
  const [websiteHoursOpen, setWebsiteHoursOpen] = React.useState(false);
  const [syncing, setSyncing] = React.useState(false);

  const syncBolt = async () => {
    setSyncing(true);
    try {
      const r: any = await ExternalOrderService.boltSyncHours(shopId);
      snackbar.open(`Sent ${r?.hoursSent ?? ''} days of hours to Bolt`);
    } catch (e) {
      snackbar.error(e);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <>
      <Paper className={classes.card} variant="outlined">
        <Typography variant="subtitle1" className={classes.title}>
          Bolt opening hours
        </Typography>
        <Typography variant="body2" className={classes.body}>
          The hours Bolt takes orders on, and the hours a scheduled job uses to
          switch Bolt online and offline for you. Set them to the kitchen's own
          hours: an app that is open before the kitchen is takes orders nobody
          can accept, and the platform cancels them within minutes. Press Sync
          Hours after changing them to send them to Bolt.
        </Typography>
        <div className={classes.actions}>
          <Button
            variant="outlined"
            startIcon={<ScheduleIcon />}
            onClick={() => setBoltHoursOpen(true)}
          >
            Edit Bolt hours
          </Button>
          <Button variant="contained" color="primary" disabled={syncing} onClick={syncBolt}>
            {syncing ? 'Sending…' : 'Sync hours to Bolt'}
          </Button>
        </div>
      </Paper>

      <Paper className={classes.card} variant="outlined">
        <Typography variant="subtitle1" className={classes.title}>
          Website ordering hours
        </Typography>
        <Typography variant="body2" className={classes.body}>
          When pekingmalta.com/order takes orders. The page opens and closes
          itself on these times; outside them customers can still order ahead
          for the next opening. Separate from the restaurant hours used by
          reservations.
        </Typography>
        <div className={classes.actions}>
          <Button
            variant="outlined"
            startIcon={<ScheduleIcon />}
            onClick={() => setWebsiteHoursOpen(true)}
          >
            Edit website hours
          </Button>
        </div>
      </Paper>

      <Paper className={classes.card} variant="outlined">
        <Typography variant="subtitle1" className={classes.title}>
          Wolt opening hours
        </Typography>
        <Typography variant="body2" className={classes.body}>
          Wolt's own opening times are held by Wolt and changed in their
          partner portal. What we do control is availability: a scheduled job
          brings the Wolt venue online when the restaurant opens and takes it
          offline when it closes, using the restaurant hours in Settings &gt;
          Shops. Wolt also reports its real status back to us, which is why
          the Wolt light on the board can be trusted.
        </Typography>
      </Paper>

      <BoltHoursDialog open={boltHoursOpen} onClose={() => setBoltHoursOpen(false)} />
      <WebsiteHoursDialog
        open={websiteHoursOpen}
        onClose={() => setWebsiteHoursOpen(false)}
      />
    </>
  );
};
